import { db } from '@/lib/firebase';
import { 
  doc, 
  collection, 
  getDoc, 
  getDocs, 
  onSnapshot, 
  Timestamp,
} from 'firebase/firestore';

/**
 * Interface representing a Policy document.
 */
export interface Policy {
  id: string;
  policyName: string;
  dateAdded?: Date | Timestamp;
  dateUpdated?: Date | Timestamp;
}

/**
 * Interface representing a Supporting Document.
 */
export interface SupportingDocument {
  id: string;
  fileName: string;
  dateAdded?: Date | Timestamp;
  dateUpdated?: Date | Timestamp;
  [key: string]: any; // For additional properties
}

/**
 * Interface representing a Key Document.
 */
export interface KeyDocument {
  id: string;
  cardTitle: string;
  dateAdded?: Date | Timestamp;
  dateUpdated?: Date | Timestamp;
  [key: string]: any; // For additional properties
}

/**
 * Interface representing all Documentation Data.
 */
export interface DocumentationData {
  policies: Policy[];
  supportingDocuments: SupportingDocument[];
  keyDocuments: KeyDocument[];
}

/**
 * Fetches all policies from Firestore.
 * @returns A promise that resolves to an array of Policy objects.
 */
export async function fetchPolicies(companyID: string): Promise<Policy[]> {
  try {
    const policiesCollection = collection(
      db,
      `customers/${companyID}/product/policyPackages/policies`
    );

    const snapshot = await getDocs(policiesCollection);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      policyName: doc.data().policy_name || 'Unnamed Policy',
      dateAdded: doc.data().dateAdded,
      dateUpdated: doc.data().dateUpdated,
      policy_history: doc.data().policy_history || {}
    }));
  } catch (error) {
    console.error('Error fetching policies:', error);
    throw new Error('Failed to load policies');
  }
}

/**
 * Fetches all supporting documents from Firestore.
 * @returns A promise that resolves to an array of SupportingDocument objects.
 */
export async function fetchSupportingDocuments(companyID: string): Promise<SupportingDocument[]> {
  try {
    const otherDocsRef = doc(
      db,
      'customers',
      companyID,
      'product',
      'policyPackages',
      'ISO27001keyDocuments',
      'otherDocs'
    );

    const otherDocsSnapshot = await getDoc(otherDocsRef);
    if (!otherDocsSnapshot.exists() || !otherDocsSnapshot.data()?.uploaded) {
      return [];
    }

    const uploaded = otherDocsSnapshot.data()?.uploaded as { [key: string]: any } || {};
    return Object.entries(uploaded).map(([id, docData]) => ({
      id,
      fileName: docData.fileName || 'Unnamed Document',
      dateAdded: docData.dateAdded,
      dateUpdated: docData.dateUpdated,
      ...docData
    }));
  } catch (error) {
    console.error('Error fetching supporting documents:', error);
    throw new Error('Failed to load supporting documents');
  }
}

/**
 * Fetches all key documents from Firestore.
 * @returns A promise that resolves to an array of KeyDocument objects.
 */
export async function fetchKeyDocuments(companyID: string): Promise<KeyDocument[]> {
  try {
    const keyDocsRef = doc(
      db,
      'customers',
      companyID,
      'product',
      'policyPackages',
      'ISO27001keyDocuments',
      'keyDocuments'
    );

    const keyDocsSnapshot = await getDoc(keyDocsRef);
    if (!keyDocsSnapshot.exists() || !keyDocsSnapshot.data()?.keyDocuments) {
      return [];
    }

    const keyDocsData = keyDocsSnapshot.data()?.keyDocuments as { [key: string]: any } || {};
    return Object.entries(keyDocsData).map(([id, docData]) => ({
      id,
      cardTitle: docData.cardTitle || 'Unnamed Document',
      dateAdded: docData.dateAdded,
      dateUpdated: docData.dateUpdated,
      ...docData
    }));
  } catch (error) {
    console.error('Error fetching key documents:', error);
    throw new Error('Failed to load key documents');
  }
}

/**
 * Fetches all documentation data (policies, supporting documents, and key documents).
 * @returns A promise that resolves to a DocumentationData object.
 */
export async function fetchAllDocumentation(companyID: string): Promise<DocumentationData> {
  try {
    const [policies, supportingDocuments, keyDocuments] = await Promise.all([
      fetchPolicies(companyID),
      fetchSupportingDocuments(companyID),
      fetchKeyDocuments(companyID)
    ]);

    return {
      policies,
      supportingDocuments,
      keyDocuments
    };
  } catch (error) {
    console.error('Error fetching documentation:', error);
    throw new Error('Failed to load documentation');
  }
}

/**
 * Sets up a real-time listener for documentation changes.
 * @param callback - Function to call when documentation data changes.
 * @returns A function to unsubscribe from the listener.
 */
export function onDocumentationChange(
  companyID: string,
  callback: (data: DocumentationData) => void
): () => void {
  const policiesUnsubscribe = onSnapshot(
    collection(db, `customers/${companyID}/product/policyPackages/policies`),
    async () => {
      const data = await fetchAllDocumentation(companyID);
      callback(data);
    }
  );

  const supportingDocsUnsubscribe = onSnapshot(
    doc(db, `customers/${companyID}/product/policyPackages/ISO27001keyDocuments/otherDocs`),
    async () => {
      const data = await fetchAllDocumentation(companyID);
      callback(data);
    }
  );

  const keyDocsUnsubscribe = onSnapshot(
    doc(db, `customers/${companyID}/product/policyPackages/ISO27001keyDocuments/keyDocuments`),
    async () => {
      const data = await fetchAllDocumentation(companyID);
      callback(data);
    }
  );

  // Return cleanup function
  return () => {
    policiesUnsubscribe();
    supportingDocsUnsubscribe();
    keyDocsUnsubscribe();
  };
}

export default {
  fetchPolicies,
  fetchSupportingDocuments,
  fetchKeyDocuments,
  fetchAllDocumentation,
  onDocumentationChange
};
